import _ from 'underscore';
import styled from 'styled-components';
import { Container, Row, Col, Heading, Span, Stack, H2 } from '@workramp/ui';
import { theme as oldTheme, WithBorderBoxCss } from '../common/utilities';

const StyledAdminNoSidebarContentWrapper = styled.div`
  ${WithBorderBoxCss}
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 3.5rem 10%;
  background: ${oldTheme.colors.admin.adminContentWrapperBackground};
`;

export const AdminNoSidebarContentWrapper = ({ children, ...props }) => (
  <StyledAdminNoSidebarContentWrapper {...props}>{children}</StyledAdminNoSidebarContentWrapper>
);

const StyledAdminContentHeadingWrapper = styled.div`
  display: flex;
  column-gap: ${({ theme }) => theme.spacing(12)}px;
  width: 100%;
  align-items: baseline;
  margin-bottom: 1rem;
`;

/**
 * Component to be used for the heading of all
 * Admin pages.
 *
 * @param { headerContent } JSX
 * @param { metaTexts } String
 * @param { lastChild } JSX
 * @returns
 */
const UnstyledAdminContentHeading = ({
  headerContent,
  metaTexts,
  lastChild,
  wrapperProps,
  className,
}: {
  headerContent: React.ReactNode;
  metaTexts?: string[];
  lastChild?: React.ReactNode;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  className?: string;
}) => (
  <StyledAdminContentHeadingWrapper className={className} {...wrapperProps}>
    <PageHeading>{headerContent}</PageHeading>
    {!_.isEmpty(metaTexts) && <AdminContentHeadingMetas metaTexts={metaTexts} wrapperProps={undefined} />}
    {lastChild && <AdminContentHeadingLastChild lastChild={lastChild} />}
  </StyledAdminContentHeadingWrapper>
);

export const NewUiContentHeader = ({ headerContent, metaTexts, lastChild }) => (
  <Row justify="space-between" align="center">
    <Col xs="auto">
      <Row gapX={2} align="center">
        <Col xs="auto">
          <PageHeading>{headerContent}</PageHeading>
        </Col>
        <Col xs="auto">
          {!_.isEmpty(metaTexts) && <AdminContentHeadingMetas metaTexts={metaTexts} wrapperProps={undefined} />}
        </Col>
      </Row>
    </Col>
    <Col xs="auto">{lastChild && <AdminContentHeadingLastChild lastChild={lastChild} />}</Col>
  </Row>
);

// Wrap subcomponents in styled constructor for CSS targeting
export const AdminContentHeading = styled(UnstyledAdminContentHeading)``;

const StyledAdminContentHeadingMetas = styled.span`
  font-size: 1rem;
  font-weight: normal;
  display: inline-block;
  color: ${oldTheme.colors.admin.adminContentHeadingText};
`;

/**
 * Component for rendering a series
 * of texts alongside a header on an Admin
 * page
 *
 * @param { metaTexts } []String
 * @returns
 */
const AdminContentHeadingMetas = ({ metaTexts, wrapperProps }) => {
  if (_.isEmpty(metaTexts)) {
    return null;
  }

  return (
    <StyledAdminContentHeadingMetas {...wrapperProps}>
      {metaTexts.map((metaText) => (
        <AdminContentHeadingMetasMetaBlock key={metaText} metaText={metaText} />
      ))}
    </StyledAdminContentHeadingMetas>
  );
};

const StyledAdminContentHeadingMetasMetaBlock = styled.span`
  margin-left: 20px;
  display: inline-block;
`;

/**
 * Component for rendering a metadata-like
 * text as part of an Admin page header
 * @param { metaText} String
 * @returns
 */
const AdminContentHeadingMetasMetaBlock = ({ metaText, ...props }) => (
  <StyledAdminContentHeadingMetasMetaBlock {...props}>{metaText}</StyledAdminContentHeadingMetasMetaBlock>
);

const StyledAdminContentHeadingLastChild = styled.div`
  flex-shrink: 0;
  margin-left: auto;
`;

const AdminContentHeadingLastChild = ({ lastChild, ...props }) => (
  <StyledAdminContentHeadingLastChild {...props}>{lastChild}</StyledAdminContentHeadingLastChild>
);

const StyledModalContentWrapper = styled.div`
  margin-top: 32px;
`;

export const ModalContentWrapper = ({ children, ...props }) => (
  <StyledModalContentWrapper {...props}>{children}</StyledModalContentWrapper>
);

export const StyledPageContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.surface[0]};
  min-height: calc(100dvh - 168px);
`;

export const StyledPageInnerContainer = styled(Container)`
  max-width: ${({ theme }) => theme.spacing(260)}px;
  margin-left: auto;
  margin-right: auto;
  padding-top: ${({ theme }) => theme.spacing(10)}px;
  padding-bottom: ${({ theme }) => theme.spacing(10)}px;

  @media (min-width: 1280px) {
    max-width: 75vw;
  }
`;

export const SettingsSubSection = ({
  title,
  description = '',
  children,
}: {
  title: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <Row align="flex-start">
    <Col xs={4}>
      <Stack size={2}>
        <H2 size={2} weight="semibold">
          {title}
        </H2>
        {description}
      </Stack>
    </Col>
    <Col xs={8}>{children}</Col>
  </Row>
);

export const StyledPageHeading = styled(Heading)`
  color: ${({ theme }) => theme.colors.on[900]};
  margin-top: 0;
  margin-bottom: 0;
  min-width: 0;
  word-wrap: break-word;
`;

export const PageHeading = ({ children }) => <StyledPageHeading level="md">{children}</StyledPageHeading>;

export const StyledLinkText = styled(Span)`
  color: ${({ theme }) => theme.colors.primary[600]};

  &:hover {
    cursor: pointer;
  }
`;
